import React, { useState, useCallback, useRef, useEffect } from "react";
import { Drawer, useMediaQuery } from "@mui/material";
import ThreadListPagination from "./threadListPagination";
import { RuleBasedThread } from "../../../types/ruleBasedInbox";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import chatStyles from "../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import inboxControlStyles from "../../../assets/styles/pageStyles/ruleBasedInbox/inboxControls/inboxControls.module.scss";
import { useShallow } from "zustand/react/shallow";
import ThreadListHeader from "./threadListHeader";
import ThreadListBody from "./threadListBody";
import InboxControls from "../inboxControls/InboxControls";

interface ThreadListContainerProps {
    isLoading: boolean;
    onThreadSelect: (thread: RuleBasedThread) => void;
    flagMobile?: boolean;
}

/**
 * Thread List Container Component
 * Shell for layout, mobile visibility, and inbox controls drawer.
 * Search and list subscribe to store data in their own child components.
 */
const ThreadListContainer: React.FC<ThreadListContainerProps> = ({
    isLoading,
    onThreadSelect,
    flagMobile,
}) => {

    // State for inbox controls drawer
    const [isControlsOpen, setIsControlsOpen] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [listHeight, setListHeight] = useState<number>(500);

    const mobileScreen = useMediaQuery("(max-width : 1000px) and (min-width : 0px)");
    const smallScreen = useMediaQuery("(max-width : 1250px) and (min-width : 1000px)");

    const { chatClicked, chatInfoClicked } = useRuleBasedInboxStore(useShallow((state) => ({
        chatClicked: state.chatClicked,
        chatInfoClicked: state.chatInfoClicked,
    })));

    // Calculate list height based on container
    useEffect(() => {
        const calculateHeight = () => {
            if (containerRef.current) {
                const containerRect = containerRef.current.getBoundingClientRect();
                // Subtract header (approx 72px), search bar (approx 60px), and pagination (approx 52px)
                const availableHeight = window.innerHeight - containerRect.top - 184;
                setListHeight(Math.max(availableHeight, 300));
            }
        };

        calculateHeight();
        window.addEventListener("resize", calculateHeight);

        return () => {
            window.removeEventListener("resize", calculateHeight);
        };
    }, []);

    const handleFilterClick = useCallback(() => {
        setIsControlsOpen(true);
    }, []);

    const handleCloseControls = useCallback(() => {
        setIsControlsOpen(false);
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                width: flagMobile ? (smallScreen ? "50%" : "100%") : undefined,
                position: "relative",
            }}
            className={`
                ${chatStyles.chatUserThreadListContainer}
                ${!mobileScreen ? chatStyles.chatUserThreadListContainerDesktop : ""}
                ${flagMobile ? chatStyles.chatUserThreadListContainerFlagMobile : chatStyles.ruleBasedThreadListContainterDesktop}
                ${flagMobile && smallScreen ? chatStyles.chatUserThreadListContainerFlagMobileSmallScreen : ""}
                ${flagMobile && !smallScreen && !(chatClicked || chatInfoClicked) ? chatStyles.chatUserThreadListContainerFlagMobileNotSmallScreen : ""}
                ${flagMobile && !smallScreen && (chatClicked || chatInfoClicked) ? chatStyles.chatUserThreadListContainerHidden : ""}
            `}
        >
            <ThreadListHeader
                onFilterClick={handleFilterClick}
            />

            <ThreadListBody
                isLoading={isLoading}
                onThreadSelect={onThreadSelect}
                listHeight={listHeight}
            />

            {!isLoading && (
                <ThreadListPagination />
            )}

            <Drawer
                anchor="left"
                open={isControlsOpen}
                onClose={handleCloseControls}
                container={containerRef.current}
                className={inboxControlStyles.inboxControlsDrawer}
                ModalProps={{
                    style: { position: "absolute" },
                    container: containerRef.current || undefined,
                }}
                BackdropProps={{
                    style: { position: "absolute" },
                    className: inboxControlStyles.drawerBackdrop,
                }}
            >
                {/* Mount only while open so store/context subscriptions are not kept alive when closed */}
                {isControlsOpen && (
                    <InboxControls onClose={handleCloseControls} />
                )}
            </Drawer>
        </div>
    );
};

export default ThreadListContainer;  
